import { ExperienceTimes } from 'components/business/search/ExperienceTimes';
import { FunctionComponent, useMemo } from 'react';
import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { LocalDateTime } from 'js-joda';
import { LockOptions } from 'types';
import { SearchSizeType } from 'utils/searchBar';
import { find } from 'es-toolkit/compat';
import { lockSelector } from 'selectors/lockSelectors';
import { useBusinessCalendar } from 'hooks/calendar/useBusinessCalendar';
import { useSelector } from 'react-redux';
import BallPulse from '@tock/shared/components/BallPulse';
import ExperienceModalStatus from 'components/business/ExperienceModalStatus';
import ReservationSearchBar from 'components/business/landing-page/ReservationSearchBar/ReservationSearchBar';

interface Props {
  business: consumer.IBusinessConfig;
  canChangePartySize?: boolean;
  className?: string;
  experienceInfo?: core.ITicketType;
  hasInitialFocus?: boolean;
  isSearching: boolean;
  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  offerings?: consumer.ConsumerOfferings;
  onSearch(size: number, date: string, time: string, seatingAreaId?: number): void;
  onSelectTime(dateTime: LocalDateTime, priceCents: number, options: LockOptions): Promise<void>;
  onNavigateToNextDate?(nextDate: string, experience: core.ITicketType): void;
  rescheduleOffering?: consumer.Offering;
  selectedDate: string;
  selectedPurchaseSize: number;
  selectedSeatingArea?: number;
  waitlistUrl?: string;
  purchase?: core.PastPurchase;
  ticketTypeIdOverride?: number;
}

const ReservationPreCheckoutDetails: FunctionComponent<Props & IWithContextProps> = ({
  business,
  canChangePartySize = true,
  className,
  experienceInfo,
  hasInitialFocus,
  isSearching,
  lockedDateTime,
  lockedSeatingArea,
  offerings,
  onSearch,
  onSelectTime,
  onNavigateToNextDate,
  rescheduleOffering,
  selectedDate,
  selectedPurchaseSize,
  selectedSeatingArea,
  waitlistUrl,
  purchase,
  ticketTypeIdOverride,
}) => {
  const isReschedule = !!rescheduleOffering;

  const {
    ticketTypePartySizeDates,
    ticketTypeDates,
    selectedDateStats: {
      selectedExperienceTimes: {
        availableTimes,
        soldTimes,
        lockedTimes,
        offeredTimes,
        specialExclusiveTimes,
        availableCommunalTicketCountsByTime,
      },
    },
    search: {
      params: { ticketTypeId },
    },
  } = useBusinessCalendar(
    {
      purchase,
      ticketTypeId: ticketTypeIdOverride,
    },
    // Reschedule route handles selection parameters via it's own state
    isReschedule ? { selectedPurchaseSize, selectedDate } : {}
  );

  const availableDates = ticketTypePartySizeDates?.availableDates;
  const lockedDates = ticketTypePartySizeDates?.lockedDates;
  const soldDates = ticketTypePartySizeDates?.soldDates;
  const offeredDates = ticketTypeDates;

  const lock = useSelector(lockSelector);

  // this is double calculated in the case of reschedule. can we hoist this up?
  const experience = useMemo(
    () => find(offerings?.experience, (experience) => experience.id === ticketTypeId),
    [offerings?.experience, ticketTypeId]
  );

  const isShipping = !!experience?.reserveShippingTime;

  return (
    <div>
      <div className="SearchBarModalContainer">
        <ReservationSearchBar
          canChangePartySize={canChangePartySize}
          hasNoTimeField
          hasExpandedCalendar={!isShipping}
          hasInitialFocus={hasInitialFocus}
          isInstant
          availableDates={availableDates}
          business={business}
          experienceInfo={experienceInfo}
          date={selectedDate}
          lockedDates={lockedDates}
          offeredDates={offeredDates}
          offerings={offerings}
          size={selectedPurchaseSize}
          soldDates={soldDates}
          ticketTypeId={ticketTypeId}
          onSearch={onSearch}
          searchSizeType={SearchSizeType.PURCHASE_SIZE}
          seatingAreaId={selectedSeatingArea}
        />
      </div>

      <div className="SearchModal-body">
        {isSearching && (
          <div className="SearchModal-bodyOverlay">
            <BallPulse position="INLINE" />
          </div>
        )}

        {onNavigateToNextDate && (
          <div>
            <ExperienceModalStatus
              waitlistUrl={waitlistUrl || ''}
              onNavigateToNextDate={onNavigateToNextDate}
              business={business}
              ticketTypeIdOverride={ticketTypeIdOverride}
            />
          </div>
        )}

        <div className={className}>
          {selectedDate && availableTimes && lockedTimes && offeredTimes && soldTimes && (
            <ExperienceTimes
              availableCommunalTicketCountsByTime={availableCommunalTicketCountsByTime || {}}
              availableTimes={availableTimes}
              experience={experience}
              isShipping={isShipping}
              lockedDateTime={lockedDateTime}
              lockedSeatingArea={lockedSeatingArea}
              lockedTimes={lockedTimes}
              lockRequested={lock?.context?.dateTimeString}
              lockRequestInProgress={lock.requestInProgress}
              offeredTimes={offeredTimes}
              onSelect={(dateTime, priceCents, options) =>
                onSelectTime(dateTime, priceCents, {
                  ...options,
                  seatingAreaId: selectedSeatingArea || options.seatingAreaId,
                })
              }
              rescheduleOffering={rescheduleOffering}
              selectedDate={selectedDate}
              selectedSize={selectedPurchaseSize}
              showExclusive={false}
              showWaitlist={!!business.showWaitlist}
              soldTimes={soldTimes}
              specialExclusiveTimes={specialExclusiveTimes}
              waitlistUrl={waitlistUrl}
              seatingAreas={experience?.seatingArea || []}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default withContext(ReservationPreCheckoutDetails);
